/*
 * RelatedMeetingsTab — reusable reverse-link surface
 *
 * Renders the list of meetings whose agenda items link to a given satellite
 * record (Incident, CAPA Issue, Risk, Control, Audit Finding, Obligation,
 * Policy, etc.). Powered by the polymorphic GOV_agenda_links table via the
 * backend endpoint:
 *   GET /api/v1/gov/related-meetings?item_type_code={code}&item_record_id={id}
 *
 * SPEC reference: MEETINGS-SPEC.md R20.
 * CONTEXT references: D9 (endpoint), D10 (7 satellite mount points).
 *
 * Props are intentionally minimal so any detail-view (IRG, CAP, COM, RSK,
 * CTR, ADT, GOV) can mount this tab with one line:
 *   <RelatedMeetingsTab itemTypeCode="INCIDENT" itemRecordId={record.id} />
 *
 * Row-click opens the existing MeetingDetailModal in "participant" view mode.
 * MeetingDetailModal is loaded via React.lazy() to avoid a circular dependency
 * (MeetingDetailModal already imports many things from this governance tree).
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Suspense,
} from "react";
import Skeleton from "../common/Skeleton";
import Badge from "../common/Badge";
import NoDataFound from "../common/NoDataFound";
import {
  RefreshCw,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import { statusVariant } from "../../utils/statusVariant";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import {
  relatedMeetingsService,
  governanceMeetingsService,
  RelatedMeeting,
  GovernanceMeeting,
} from "../../services/governanceService";

// Lazy import to avoid circular dependency with MeetingDetailModal (which
// itself pulls in many governance components). The lazy boundary defers the
// import to the first row-click instead of evaluating at module-load time.
const MeetingDetailModal = React.lazy(() => import("./MeetingDetailModal"));

export interface RelatedMeetingsTabProps {
  /** UTL_entity_registry.entity_code value, e.g. "INCIDENT", "CAPA_ISSUE". */
  itemTypeCode: string;
  /** Record id of the satellite record (e.g. incident.id). */
  itemRecordId: number | string;
  /** Optional hard cap forwarded to the backend (default 100). */
  limit?: number;
}

type SortKey =
  | "meeting_code"
  | "meeting_title"
  | "meeting_type"
  | "meeting_status"
  | "meeting_date"
  | "relationship_type"
  | "agenda_name";

type SortDir = "asc" | "desc";

const RelatedMeetingsTab: React.FC<RelatedMeetingsTabProps> = ({
  itemTypeCode,
  itemRecordId,
  limit = 100,
}) => {
  const { formatDate } = useEntitySettings();
  const [rows, setRows] = useState<RelatedMeeting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>("meeting_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Row-click meeting modal state.
  const [selectedMeeting, setSelectedMeeting] =
    useState<GovernanceMeeting | null>(null);
  const [meetingLoading, setMeetingLoading] = useState<boolean>(false);
  const [meetingLoadError, setMeetingLoadError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await relatedMeetingsService.getRelatedMeetings(
        itemTypeCode,
        itemRecordId,
        limit,
      );
      setRows(data);
    } catch (e: any) {
      setError(e?.message || "Failed to load related meetings");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [itemTypeCode, itemRecordId, limit]);

  // Fetch on mount + whenever props change.
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle sort direction when same column clicked; otherwise switch column.
  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = (a[sortKey] ?? "") as string | number;
      const bv = (b[sortKey] ?? "") as string | number;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  // Row-click: fetch the full meeting payload then open MeetingDetailModal.
  const handleRowClick = useCallback(async (meetingId: number) => {
    setMeetingLoading(true);
    setMeetingLoadError(null);
    try {
      const meeting = await governanceMeetingsService.getById(meetingId);
      setSelectedMeeting(meeting);
    } catch (e: any) {
      setMeetingLoadError(e?.message || "Failed to open meeting");
    } finally {
      setMeetingLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedMeeting(null);
  }, []);

  // onMeetingUpdated: refetch the related-meetings list so e.g. status
  // changes (CLOSED) reflect immediately.
  const handleMeetingUpdated = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div className="related-meetings-tab">
      {/* Header with refresh */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.5rem",
        }}
      >
        <h4 style={{ margin: 0 }}>

          {!loading && rows.length > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: "0.75rem",
                color: "var(--text-secondary, #6b7280)",
                fontWeight: 400,
              }}
            >
              ({rows.length})
            </span>
          )}
        </h4>
        <button
          data-testid="BTN_related_meetings_refresh"
          type="button"
          className="btn-primary-fill"
          onClick={fetchData}
          disabled={loading}
          title="Refresh"

        >
          <RefreshCw size={12} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Error banner with retry */}
      {error && (
        <div
          className="error-banner"
          style={{
            padding: "0.75rem",
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 4,
            marginBottom: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#991b1b",
            fontSize: "0.85rem",
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <AlertCircle size={14} />
            {error}
          </span>
          <button
            data-testid="BTN_related_meetings_error_retry"
            type="button"
            className="btn-secondary"
            onClick={fetchData}
            style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Meeting-open error (row click failure) */}
      {meetingLoadError && (
        <div
          className="error-banner"
          style={{
            padding: "0.5rem 0.75rem",
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 4,
            marginBottom: "0.75rem",
            color: "#991b1b",
            fontSize: "0.8rem",
          }}
        >
          {meetingLoadError}
        </div>
      )}

      {/* Table */}
      <div className="table-list" style={{ display: "flex", flexDirection: "column" }}>
        <div
          className="table-list-scroll"
          style={!loading && !error && sortedRows.length === 0 ? { flex: "0 0 auto", minHeight: 0 } : undefined}
        >
          <table className="table-list-table">
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <th
                  data-testid="BTN_related_meetings_sort_code"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("meeting_code")}
                >
                  Code{sortArrow("meeting_code")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_title"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("meeting_title")}
                >
                  Title{sortArrow("meeting_title")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_type"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("meeting_type")}
                >
                  Type{sortArrow("meeting_type")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_date"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("meeting_date")}
                >
                  Date{sortArrow("meeting_date")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_relationship"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("relationship_type")}
                >
                  Relationship{sortArrow("relationship_type")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_agenda"
                  className="table-list-th"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("agenda_name")}
                >
                  Agenda{sortArrow("agenda_name")}
                </th>
                <th
                  data-testid="BTN_related_meetings_sort_status"
                  className="table-list-th table-list-cell--status"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("meeting_status")}
                >
                  Status{sortArrow("meeting_status")}
                </th>
              </tr>
            </thead>
            <tbody aria-busy={loading} aria-live="polite">
              {loading && <Skeleton.TableRows cols={['3xs', '3xs', 'md', 'sm', 'sm', 'xs', 'sm', 'pill']} />}

              {!loading && !error && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="table-list-cell">
                    <NoDataFound
                      title="No related meetings"
                      message="No meetings are linked to this record yet."
                    />
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, idx) => (
                  <tr
                    key={`${r.meeting_id}-${r.agenda_id}`}
                    data-testid={`BTN_related_meetings_row_${r.meeting_id}_open`}
                    className="table-list-row"
                    onClick={() => handleRowClick(r.meeting_id)}
                    style={{ cursor: "pointer" }}
                    title="Click to open meeting"
                  >
                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                    <td className="table-list-cell">{r.meeting_code}</td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title">
                        {r.meeting_title}
                      </span>
                    </td>
                    <td className="table-list-cell">{r.meeting_type}</td>
                    <td className="table-list-cell">
                      {formatDate(r.meeting_date)}
                    </td>
                    <td className="table-list-cell">
                      {r.relationship_type || "—"}
                    </td>
                    <td className="table-list-cell">
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {r.agenda_name}
                        <ExternalLink
                          size={10}
                          style={{ opacity: 0.5, flexShrink: 0 }}
                        />
                      </span>
                    </td>
                    <td className="table-list-cell table-list-cell--status">
                      <Badge variant={statusVariant(r.meeting_status)} size="sm">
                        {(r.meeting_status || "").replace("_", " ")}
                      </Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Row-click load indicator */}
      {meetingLoading && (
        <div
          style={{
            padding: "0.5rem",
            textAlign: "center",
            fontSize: "0.8rem",
            color: "var(--text-secondary, #6b7280)",
          }}
        >
          Loading meeting…
        </div>
      )}

      {/* Lazy-loaded MeetingDetailModal in participant view-mode */}
      {selectedMeeting && (
        <Suspense fallback={null}>
          <MeetingDetailModal
            meeting={selectedMeeting}
            onClose={handleCloseModal}
            onMeetingUpdated={handleMeetingUpdated}
          />
        </Suspense>
      )}
    </div>
  );
};

export default RelatedMeetingsTab;
